import React, { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faPen } from '@fortawesome/free-solid-svg-icons'
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'


interface LevelDefinitionsComponentProps {
    userRole: 'admin' | 'user'
    defaultLevelDescriptions: Record<number, string>
}

const LevelDefinitionsComponent = ({ userRole, defaultLevelDescriptions }: LevelDefinitionsComponentProps) => {
    const [isEditingLevels, setIsEditingLevels] = useState(false)
    const [levelDescriptions, setLevelDescriptions] = useState<Record<number, string>>({})
    const [expandedLevels, setExpandedLevels] = useState<number[]>([])

    const toggleLevel = (level: number) => {
        setExpandedLevels((prev) =>
            prev.includes(level) ? prev.filter((item) => item !== level) : [...prev, level]
        )
    }

    const handleLevelDescriptionChange = (level: number, value: string) => {
        setLevelDescriptions((prev) => ({
            ...prev,
            [level]: value,
        }))
    }

    const handleSaveLevelDescriptions = () => {
        setIsEditingLevels(false)
    }

    const handleCancelLevelEdit = () => {
        setLevelDescriptions(defaultLevelDescriptions)
        setIsEditingLevels(false)
    }

    const hasLevelChanges = useMemo(
        () => JSON.stringify(levelDescriptions) !== JSON.stringify(defaultLevelDescriptions),
        [defaultLevelDescriptions, levelDescriptions]
    )

    return userRole === 'admin' ? (
        <div className="card shadow-sm mt-4 gw-assessment-levels">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="fw-semibold mb-0">Level Definitions</h3>
                    <div className="d-flex gap-2">
                        {isEditingLevels ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleCancelLevelEdit}
                                    className="btn btn-light btn-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveLevelDescriptions}
                                    disabled={!hasLevelChanges}
                                    className={`btn btn-sm gw-btn-purple ${!hasLevelChanges ? 'disabled' : ''
                                        }`}
                                >
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    Save Changes
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsEditingLevels(true)}
                                className="btn btn-light btn-sm gw-btn-purple-outline"
                            >
                                <FontAwesomeIcon icon={faPen} className="me-1" />
                                Edit Definitions
                            </button>
                        )}
                    </div>
                </div>
                <div className="d-flex flex-column gap-2">
                    {Object.entries(levelDescriptions).map(([level, description]) => {
                        const levelNum = parseInt(level, 10)
                        const isExpanded = expandedLevels.includes(levelNum)

                        return (
                            <div key={level} className="gw-level-definition-card">
                                <button
                                    type="button"
                                    onClick={() => toggleLevel(levelNum)}
                                    className="gw-level-definition-toggle"
                                >
                                    <span className="fw-semibold small">Level {level}</span>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className="text-muted"
                                    />
                                </button>
                                {isExpanded && (
                                    <div className="gw-level-definition-body">
                                        {isEditingLevels ? (
                                            <textarea
                                                value={description}
                                                onChange={(event) =>
                                                    handleLevelDescriptionChange(
                                                        levelNum,
                                                        event.target.value
                                                    )
                                                }
                                                className="form-control gw-level-definition-textarea"
                                                rows={3}
                                            />
                                        ) : (
                                            <p className="small text-muted mb-0">{description}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    ) : null
}

export default LevelDefinitionsComponent